import { FC, useEffect, useMemo, useState } from "react";
import useIsComponentMounted from "../../../../../common/hooks/use-is-component-mounted";
import useRouteQueryParams from "../../../../../common/hooks/use-route-query-params";
import BillingsTaxiUnbilledRouteListingByRailyRouteQueryParams from "./common/types/billings-taxi-unbilled-route-listing-by-raily-route-query-params";
import BillingsTaxiUnbilledRouteListingItem from "../common/types/billings-taxi-unbilled-route-listing-item";
import BillingsTaxiUnbilledRouteListingFilter, {
  BillingsTaxiUnbilledRouteListingRouteStartDateFilter,
} from "../common/types/billings-taxi-unbilled-route-listing-filter";
import BillingsTaxiUnbilledRouteListingSortKey from "../common/types/billings-taxi-unbilled-route-listing-sort-key";
import BillingsTaxiUnbilledRouteListingSortSelectOption from "../common/types/billings-taxi-unbilled-route-listing-sort-select-option";
import billingsTaxiUnbilledRouteListingSortHelper from "../common/billings-taxi-unbilled-route-listing-sort.helper";
import usePagination from "../../../../../common/hooks/use-pagination";
import billingsTaxiUnbilledRouteListingByRailyRouteQueryParamsService from "./common/billings-taxi-unbilled-route-listing-by-raily-route-query-params.service";
import HeadingComponent from "../../../../../common/components/heading/heading.component";
import BillingsTaxiUnbilledRouteListingFiltersSelectComponent from "../common/filters/select/billings-taxi-unbilled-route-listing-filters-select.component";
import ListingSortSelectComponent from "../../../../../common/components/listing/filter/sort/select/listing-sort-select.component";
import BillingsTaxiUnbilledRouteListingFiltersBadgeListComponent from "../common/filters/list/billings-taxi-unbilled-route-listing-filters-badge-list.component";
import CardComponent from "../../../../../common/components/card/card.component";
import BillingsTaxiUnbilledRouteListingTableComponent from "../common/table/billings-taxi-unbilled-route-listing-table.component";
import PaginationComponent from "../../../../../common/components/pagination/pagination.component";
import BillingsTaxiUnbilledRouteListingViewBasicProps from "../common/types/billings-taxi-unbilled-route-listing-view-basic-props";
import billingsTaxiUnbilledRouteListingByRailyFactory from "./common/billings-taxi-unbilled-route-listing-by-raily.factory";
import BillingsTaxiUnbilledRouteListingFilterType from "../common/types/billings-taxi-unbilled-route-listing-filter-type";
import DateRange from "../../../../../common/types/date-range";
import BillingsTaxiUnbilledRouteListingByRailyTaxiCorporationSelectOption from "./common/types/billings-taxi-unbilled-route-listing-by-raily-taxi-corporation-select-option";
import FormFieldComponent from "../../../../../common/components/form/field/form-field.component";
import billingsTaxiUnbilledRouteListingRequestFactory from "../common/billings-taxi-unbilled-route-listing-request.factory";
import { useAppContext } from "../../../../../context/app.context";
import DateRangeInputComponent from "../../../../../common/components/form/input/date-range/date-range-input.component";
import SingleSelectComponent from "../../../../../common/components/form/select/single-select/single-select.component";
import billingsTranslationsHelper from "../../../../../languages/billings-translations.helper";
import useTaxiCorporationUnbilledRouteList from "../../../../../common/services/taxi-corporation/unbilled/route-list/use-taxi-corporation-unbilled-route-list";
import useAbort from "../../../../../common/hooks/use-abort";
import useTaxiCorporationList from "../../../../../common/services/taxi-corporation/list/use-taxi-corporation-list";

type BillingsTaxiUnbilledRouteListingByRailyProps =
  BillingsTaxiUnbilledRouteListingViewBasicProps;

const BillingsTaxiUnbilledRouteListingByRailyComponent: FC<
  BillingsTaxiUnbilledRouteListingByRailyProps
> = () => {
  const isComponentMounted = useIsComponentMounted();
  const { selectedAppLanguage } = useAppContext();

  const [routeQueryParams, setRouteQueryParams] =
    useRouteQueryParams<BillingsTaxiUnbilledRouteListingByRailyRouteQueryParams>();

  const translations =
    billingsTranslationsHelper.getTaxiUnbilledRouteBillingsListingTranslations();

  const [filters, setFilters] = useState<
    BillingsTaxiUnbilledRouteListingFilter[]
  >(() =>
    billingsTaxiUnbilledRouteListingByRailyRouteQueryParamsService.getFilters(
      routeQueryParams,
    ),
  );

  const taxiCorporationUnbilledRouteList =
    useTaxiCorporationUnbilledRouteList();
  const taxiCorporationUnbilledRouteListAbort = useAbort();

  const [listingItems, setListingItems] = useState<
    BillingsTaxiUnbilledRouteListingItem[]
  >([]);
  const [totalResults, setTotalResults] = useState(0);

  const [selectedSortKey, setSelectedSortKey] =
    useState<BillingsTaxiUnbilledRouteListingSortKey | null>(
      () =>
        billingsTaxiUnbilledRouteListingByRailyRouteQueryParamsService.getSortKey(
          routeQueryParams,
        ) ?? null,
    );

  const [selectedTaxiCorporationUuid, setSelectedTaxiCorporationUuid] =
    useState<string | null>(
      () =>
        billingsTaxiUnbilledRouteListingByRailyRouteQueryParamsService.getTaxiCorporationUuid(
          routeQueryParams,
        ) ?? null,
    );

  const onTaxiCorporationChange = (
    selectedOption: BillingsTaxiUnbilledRouteListingByRailyTaxiCorporationSelectOption,
  ) => {
    setSelectedTaxiCorporationUuid(selectedOption.value.uuid);
    setPage(1);
  };

  const taxiCorporationList = useTaxiCorporationList();
  const taxiCorporationListAbort = useAbort();

  const [taxiCorporationSelectOptions, setTaxiCorporationSelectOptions] =
    useState<
      BillingsTaxiUnbilledRouteListingByRailyTaxiCorporationSelectOption[]
    >([]);

  const selectedTaxiCorporationSelectOption = taxiCorporationSelectOptions.find(
    (option) => option.value.uuid === selectedTaxiCorporationUuid,
  );

  const sortSelectOptions: BillingsTaxiUnbilledRouteListingSortSelectOption[] =
    useMemo(
      () => billingsTaxiUnbilledRouteListingSortHelper.getSelectOptions(),
      [selectedAppLanguage],
    );

  const { page, pageSize, setPage, setPageSize } = usePagination({
    totalResults: totalResults,
    defaultPageSize:
      billingsTaxiUnbilledRouteListingByRailyRouteQueryParamsService.getPageSize(
        routeQueryParams,
      ),
    defaultPage:
      billingsTaxiUnbilledRouteListingByRailyRouteQueryParamsService.getPage(
        routeQueryParams,
      ),
  });

  useEffect(() => {
    const taxiCorporationsSelectOptions =
      billingsTaxiUnbilledRouteListingByRailyFactory.createTaxiCorporationSelectOptions(
        taxiCorporationList.data.data,
      );

    setTaxiCorporationSelectOptions(taxiCorporationsSelectOptions);
  }, [taxiCorporationList.data]);

  useEffect(() => {
    taxiCorporationList.load({}, taxiCorporationListAbort.signal);

    return taxiCorporationListAbort.revoke;
  }, []);

  useEffect(() => {
    if (!taxiCorporationUnbilledRouteList.data) return;

    const listingItems =
      billingsTaxiUnbilledRouteListingByRailyFactory.createListingItems(
        taxiCorporationUnbilledRouteList.data.data,
      );

    setListingItems(listingItems);
    setTotalResults(taxiCorporationUnbilledRouteList.data.totalCount);
  }, [taxiCorporationUnbilledRouteList.data]);

  const fetchListing = () => {
    if (!selectedTaxiCorporationUuid) return;

    const request =
      billingsTaxiUnbilledRouteListingRequestFactory.createRequest(
        selectedTaxiCorporationUuid,
        page,
        pageSize,
        filters,
        selectedSortKey,
      );

    taxiCorporationUnbilledRouteList.load(
      request,
      taxiCorporationUnbilledRouteListAbort.signal,
    );
  };

  useEffect(() => {
    if (!page || !pageSize || !selectedTaxiCorporationUuid) {
      return;
    }

    fetchListing();

    return taxiCorporationUnbilledRouteListAbort.revoke;
  }, [filters, selectedTaxiCorporationUuid, selectedSortKey, pageSize, page]);

  useEffect(() => {
    if (!isComponentMounted) {
      return;
    }

    const queryParams =
      billingsTaxiUnbilledRouteListingByRailyRouteQueryParamsService.createRouteQueryParams(
        selectedTaxiCorporationUuid,
        filters,
        selectedSortKey,
        page,
        pageSize,
      );

    setRouteQueryParams(queryParams);
  }, [selectedTaxiCorporationUuid, filters, selectedSortKey, page, pageSize]);

  const addNewFilter = (newFilter: BillingsTaxiUnbilledRouteListingFilter) => {
    setFilters((curr) => [...curr, newFilter]);
    setPage(1);
  };

  const deleteFilter = (index: number) => {
    const newFilters = filters.filter((filter, _index) => _index !== index);

    setFilters(newFilters);
    setPage(1);
  };

  const deleteAllFilters = () => {
    setFilters([]);
    setPage(1);
  };

  const onPageChange = (page: number) => {
    setPage(page);
    window.scroll({ top: 0, behavior: "smooth" });
  };

  const onPageSizeChange = (pageSize: number) => {
    setPageSize(pageSize);
    setPage(1);
  };

  const selectedSortSelectOption = useMemo(() => {
    return (
      sortSelectOptions.find((item) => item.value === selectedSortKey) ?? null
    );
  }, [selectedSortKey, selectedAppLanguage]);

  const startDateFilterValue = filters.find(
    (filter) =>
      filter.type ===
      BillingsTaxiUnbilledRouteListingFilterType.ORDER_START_DATE,
  )?.value as
    | BillingsTaxiUnbilledRouteListingRouteStartDateFilter["value"]
    | undefined;

  const onOrderStartDateFilterValueChange = (dateRange: DateRange | null) => {
    if (!dateRange) {
      const newFilters = filters.filter(
        (filter) =>
          filter.type !==
          BillingsTaxiUnbilledRouteListingFilterType.ORDER_START_DATE,
      );

      setFilters(newFilters);
      return;
    }

    const isFilterExists = !!filters.find(
      (filter) =>
        filter.type ===
        BillingsTaxiUnbilledRouteListingFilterType.ORDER_START_DATE,
    );

    const newFilter: BillingsTaxiUnbilledRouteListingRouteStartDateFilter = {
      type: BillingsTaxiUnbilledRouteListingFilterType.ORDER_START_DATE,
      value: {
        from: dateRange.from!,
        to: dateRange.to!,
      },
    };

    if (isFilterExists) {
      const newFilters = [
        ...filters.filter(
          (filter) =>
            filter.type !==
            BillingsTaxiUnbilledRouteListingFilterType.ORDER_START_DATE,
        ),
        newFilter,
      ];

      setFilters(newFilters);
      setPage(1);
      return;
    }

    setFilters((curr) => [...curr, newFilter]);
    setPage(1);
  };

  const isListingContentVisible = !!selectedTaxiCorporationUuid;

  return (
    <div className="billings_taxi_unbilled_route_listing">
      <HeadingComponent title={translations.header.headingText} />
      <FormFieldComponent label={translations.taxiCorporationLabel} isRequired>
        <SingleSelectComponent
          placeholder={translations.taxiCorporationPlaceholder}
          value={selectedTaxiCorporationSelectOption ?? null}
          options={taxiCorporationSelectOptions}
          onChange={(selectedOption) =>
            onTaxiCorporationChange(selectedOption!)
          }
          classNames={{
            root: "billings_taxi_unbilled_route_listing_taxi_corporation_select",
          }}
          idForTesting={`billings-taxi-unbilled-route-listing-by-raily-taxi-corporation-select`}
          isLoading={taxiCorporationList.isLoading}
          isDisabled={taxiCorporationList.isError}
          isSearchable
        />
      </FormFieldComponent>
      {isListingContentVisible && (
        <>
          <div className="billings_taxi_unbilled_route_listing_tools">
            <div className="d-flex">
              <BillingsTaxiUnbilledRouteListingFiltersSelectComponent
                filters={filters}
                onAddNewFilter={addNewFilter}
              />
              <DateRangeInputComponent
                date={startDateFilterValue ?? null}
                onChange={onOrderStartDateFilterValueChange}
                classNames={{ root: "ml-2" }}
                placeholder={
                  translations.filters.searchByStartDateSelectInputPlaceholder
                }
                idForTesting="billings-taxi-unbilled-route-listing-by-raily-date-range"
              />
            </div>
            <ListingSortSelectComponent
              onChange={(option) => setSelectedSortKey(option?.value!)}
              options={sortSelectOptions}
              value={selectedSortSelectOption}
              idForTesting="billings-taxi-unbilled-route-listing-by-raily-sort"
            />
          </div>
          <BillingsTaxiUnbilledRouteListingFiltersBadgeListComponent
            filters={filters}
            onDeleteFilterClick={deleteFilter}
            onDeleteAllFiltersButtonClick={deleteAllFilters}
          />
          <CardComponent classNames={{ root: "mt-4" }}>
            <BillingsTaxiUnbilledRouteListingTableComponent
              listingItems={listingItems}
              isError={taxiCorporationUnbilledRouteList.isError}
              isLoading={taxiCorporationUnbilledRouteList.isLoading}
            />
            <div className="billings_taxi_unbilled_route_listing__pagination_wrapper">
              <PaginationComponent
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
                page={page}
                pageSize={pageSize}
                totalResults={totalResults}
              />
            </div>
          </CardComponent>
        </>
      )}
    </div>
  );
};

export default BillingsTaxiUnbilledRouteListingByRailyComponent;
